(function (scope) {
  'use strict';
  function fault(code, message, uncertain = false) {
    return Object.assign(new Error(message || code), {code, uncertain});
  }
  function rootPath() { return scope.Shopify?.routes?.root || '/'; }
  const submissions = new Map();
  const submissionListeners = new Map();
  const cartVisitKey = 'melco-cart-visit';
  function cartVisit() { try { return scope.sessionStorage?.getItem(cartVisitKey) || null; } catch { return null; } }
  if (scope.location?.pathname?.replace(/\/+$/, '').endsWith('/cart')) {
    try { scope.sessionStorage?.setItem(cartVisitKey, String(Date.now()) + '-' + Math.random()); } catch {}
  }
  function notifySubmission(handle, reset = false) { submissionListeners.get(handle)?.forEach(listener => listener(reset)); }
  function restoreAfterNavigation(event) {
    if (!event.persisted) return;
    const visit = cartVisit();
    for (const [handle, record] of submissions) {
      if (record.phase === 'complete' || (record.phase === 'uncertain' && visit && visit !== record.cartVisit)) {
        submissions.delete(handle); notifySubmission(handle, true);
      }
    }
  }
  scope.addEventListener?.('pageshow', restoreAfterNavigation);
  function variantsOf(product) {
    if (!product || !Array.isArray(product.variants)) throw fault('UNAVAILABLE');
    return product.variants.map(variant => ({
      id: Number(variant.id),
      size: (Array.isArray(variant.options) ? variant.options.join(' / ') : variant.title) || variant.size || 'Standart',
      price: Number(variant.price),
      available: variant.available === true,
      sku: variant.sku || ''
    })).filter(variant => Number.isSafeInteger(variant.id) && variant.id > 0 && Number.isFinite(variant.price));
  }
  async function readProduct(handle, fetcher = scope.fetch.bind(scope)) {
    if (!handle || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) throw fault('UNAVAILABLE');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetcher(rootPath() + 'products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}, signal: controller.signal
      });
      if (!response.ok) throw fault(response.status === 404 ? 'UNAVAILABLE' : 'LOAD_FAILED');
      const product = await response.json();
      if (product.handle !== handle || !variantsOf(product).length) throw fault('UNAVAILABLE');
      return product;
    } catch (error) {
      if (error.code) throw error;
      throw fault('LOAD_FAILED');
    } finally { clearTimeout(timeout); }
  }
  async function addItem({handle, variantId, quantity, expectedPrice, timeoutMs = 12000}, fetcher = scope.fetch.bind(scope)) {
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw fault('QUANTITY');
    const product = await readProduct(handle, fetcher);
    const variant = variantsOf(product).find(item => item.id === Number(variantId));
    if (!variant || !variant.available) throw fault('SOLD_OUT');
    if (Number.isFinite(expectedPrice) && expectedPrice !== variant.price) throw Object.assign(fault('PRICE_CHANGED'), {product});
    let response, payload, timer;
    const controller = new AbortController();
    try {
      await Promise.race([
        (async () => {
          response = await fetcher(rootPath() + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin', signal: controller.signal,
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({items: [{id: variant.id, quantity}]})
          });
          payload = await response.json();
        })(),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {controller.abort(); reject(fault('OUTCOME_UNKNOWN', undefined, true));}, timeoutMs);
        })
      ]);
    } catch { throw fault('OUTCOME_UNKNOWN', undefined, true); }
    finally { clearTimeout(timer); }
    if (!response.ok || payload.status) {
      if (response.status >= 500) throw fault('OUTCOME_UNKNOWN', undefined, true);
      throw fault('CART_REJECTED', typeof payload.description === 'string' ? payload.description : '', response.status === 422 || Number(payload.status) === 422);
    }
    if (!Array.isArray(payload.items) || !payload.items.some(item => Number(item.variant_id || item.id) === variant.id)) {
      throw fault('OUTCOME_UNKNOWN', undefined, true);
    }
    return payload;
  }
  async function commitItem(options, fetcher = scope.fetch.bind(scope)) {
    const prior = submissions.get(options.handle);
    if (prior) throw prior.error || fault(prior.phase === 'complete' ? 'ALREADY_ADDED' : 'PENDING', undefined, true);
    const visit = cartVisit();
    submissions.set(options.handle, {phase: 'pending', cartVisit: visit}); notifySubmission(options.handle);
    try {
      const payload = await addItem(options, fetcher);
      submissions.set(options.handle, {phase: 'complete', cartVisit: visit}); notifySubmission(options.handle);
      return payload;
    } catch (error) {
      if (error.uncertain) submissions.set(options.handle, {phase: 'uncertain', error, cartVisit: visit});
      else submissions.delete(options.handle);
      notifySubmission(options.handle);
      throw error;
    }
  }
  function mount(area, product) {
    let disposed = false, busy = false, ready = false, uncertain = false, committed = false, currentVariants = [], choice = null;
    const words = area.dataset;
    const make = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const messageFor = error => (({
      UNAVAILABLE: words.unavailable, LOAD_FAILED: words.loadFailed, SOLD_OUT: words.soldOutError,
      QUANTITY: words.quantityError, PRICE_CHANGED: words.priceChanged, OUTCOME_UNKNOWN: words.unknownError
    }[error.code] || (error.message !== error.code ? error.message : '') || words.cartError) + (error.code === 'CART_REJECTED' && error.uncertain ? ' ' + words.unknownError : ''));
    const price = make('p', 'r9-product-price');
    const form = make('form', 'r9-purchase-form');
    const fieldset = make('fieldset', 'r9-size-fieldset');
    const legend = make('legend', 'r9-size-label', words.sizeLabel);
    const choices = make('div', 'r9-size-list');
    fieldset.append(legend, choices);
    const quantityLabel = make('label', 'r9-quantity-label', words.quantityLabel);
    const quantity = make('input', 'r9-quantity');
    quantity.type = 'number'; quantity.name = 'quantity'; quantity.min = '1'; quantity.step = '1';
    quantity.value = '1'; quantity.required = true; quantity.inputMode = 'numeric';
    quantityLabel.append(quantity);
    const submit = make('button', 'r9-add-to-cart', words.addLabel); submit.type = 'submit'; submit.disabled = true;
    const status = make('p', 'r9-commerce-status', words.loadingLabel);
    status.id = 'R9CommerceStatus'; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    form.setAttribute('aria-describedby', status.id);
    const retry = make('button', 'r9-commerce-retry', words.retryLabel); retry.type = 'button'; retry.hidden = true;
    const cartLink = make('a', 'r9-commerce-cart-link', words.cartLabel); cartLink.href = rootPath() + 'cart'; cartLink.hidden = true;
    const productLink = make('a', 'r9-commerce-product-link', words.productLink); productLink.href = rootPath() + 'products/' + encodeURIComponent(product.handle); productLink.hidden = true;
    form.append(fieldset, quantityLabel, submit, status, retry, cartLink, productLink);
    area.replaceChildren(price, form); area.hidden = false;
    function showPrice(amount) {
      price.hidden = !Number.isFinite(amount);
      if (!price.hidden) price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
        style: 'currency', currency: scope.Shopify?.currency?.active || product.currency || 'TRY'
      }).format(amount / 100);
    }
    function updateSubmit() {
      submit.disabled = busy || uncertain || committed || !ready || !choice?.available;
      submit.textContent = busy ? words.addingLabel : words.addLabel;
      fieldset.disabled = busy || uncertain || committed;
      quantity.disabled = busy || uncertain || committed;
    }
    function syncSubmission(reset = false) {
      if (disposed) return;
      const record = submissions.get(product.handle);
      busy = record?.phase === 'pending'; uncertain = record?.phase === 'uncertain'; committed = record?.phase === 'complete';
      if (reset) {choice = null; quantity.value = '1'; cartLink.hidden = true; load(); return;}
      if (busy) status.textContent = words.addingLabel;
      if (uncertain) status.textContent = messageFor(record.error);
      if (committed) status.textContent = words.addedLabel.replace(/\..*/, '.');
      if (record) {retry.hidden = true; cartLink.hidden = !(uncertain || committed);}
      updateSubmit();
    }
    if (!submissionListeners.has(product.handle)) submissionListeners.set(product.handle, new Set());
    submissionListeners.get(product.handle).add(syncSubmission);
    function renderVariants(variants, live) {
      const previous = choice?.id;
      currentVariants = variants;
      choices.replaceChildren();
      choice = variants.find(variant => variant.id === previous && variant.available) || null;
      if (variants.length === 1 && variants[0].available) choice = variants[0];
      for (const variant of variants) {
        const label = make('label', 'r9-size-choice' + (variant.available ? '' : ' is-unavailable'));
        const input = make('input', 'r9-size-radio');
        input.type = 'radio'; input.name = 'id'; input.value = String(variant.id); input.required = true;
        input.disabled = !variant.available; input.checked = variant.id === choice?.id;
        input.setAttribute('aria-label', variant.size + (variant.available ? '' : ' — ' + words.soldOutLabel));
        const visual = make('span', 'r9-size-item');
        visual.append(make('span', 'r9-size-name', variant.size));
        if (live) visual.append(make('span', 'r9-size-status', variant.available ? words.inStockLabel : words.soldOutLabel));
        label.append(input, visual); choices.append(label);
      }
      updateSubmit();
      syncSubmission();
    }
    function acceptLive(live) {
      ready = true;
      renderVariants(variantsOf(live), true);
      showPrice(choice?.price ?? Number(live.price));
      productLink.hidden = false;
      status.textContent = choice ? '' : (currentVariants.some(variant => variant.available) ? words.chooseLabel : words.soldOutError);
      updateSubmit();
      syncSubmission();
    }
    async function load() {
      ready = false; retry.hidden = true; status.textContent = words.loadingLabel; updateSubmit();
      try {
        const live = await readProduct(product.handle);
        if (!disposed) acceptLive(live);
      } catch (error) {
        if (disposed) return;
        ready = false; status.textContent = messageFor(error); retry.hidden = error.code !== 'UNAVAILABLE'; updateSubmit(); syncSubmission();
      }
    }
    showPrice(product.price);
    renderVariants((product.variants || []).map(variant => ({...variant, available: Number(variant.quantity) > 0})), false);
    form.addEventListener('change', event => {
      if (event.target.name !== 'id' || busy || uncertain || committed) return;
      choice = currentVariants.find(variant => variant.id === Number(event.target.value)) || null;
      if (ready && choice) {showPrice(choice.price); status.textContent = '';}
      updateSubmit();
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (disposed || busy || uncertain || committed || !ready) return;
      if (!choice?.available) {status.textContent = words.chooseLabel; choices.querySelector('input:not(:disabled)')?.focus(); return;}
      const amount = Number(quantity.value);
      if (!quantity.reportValidity() || !Number.isSafeInteger(amount) || amount < 1) {status.textContent = words.quantityError; return;}
      busy = true; status.textContent = words.addingLabel; retry.hidden = true; updateSubmit();
      try {
        await commitItem({handle: product.handle, variantId: choice.id, quantity: amount, expectedPrice: choice.price});
        if (disposed) return;
        status.textContent = words.addedLabel; cartLink.hidden = false;
        scope.location.assign(rootPath() + 'cart');
      } catch (error) {
        if (disposed) return;
        if (error.product) acceptLive(error.product);
        if (error.code === 'SOLD_OUT' || error.code === 'UNAVAILABLE') {ready = false; retry.hidden = false;}
        uncertain = error.uncertain === true;
        cartLink.hidden = !uncertain;
        status.textContent = messageFor(error);
        busy = false; updateSubmit();
      }
    });
    retry.addEventListener('click', () => { if (!busy && !uncertain && !committed) load(); });
    load();
    syncSubmission();
    return {destroy() {disposed = true; submissionListeners.get(product.handle)?.delete(syncSubmission);}};
  }
  const api = {variantsOf, readProduct, addItem, commitItem, mount, restoreAfterNavigation};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else scope.MelcoCommerce = api;
})(typeof window !== 'undefined' ? window : globalThis);
